/**
 * Voyage Formatters Composable
 * Provides formatting utility functions for voyage data display
 */

import moment from 'moment';

export function useVoyageFormatters() {
  /**
   * Format a date string to Indonesian format (DD MMMM YYYY, HH:mm:ss)
   * @param {string} dateString - Date string to format
   * @returns {string} Formatted date string
   */
  const formatIndonesianDate = (dateString) => moment(dateString).format('DD MMMM YYYY, HH:mm:ss');

  /**
   * Format a number with 2 decimal places
   * @param {number} value - Number to format
   * @returns {string} Formatted number string or 'N/A'
   */
  const formatNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';
    return Number(value).toFixed(2);
  };

  /**
   * Format distance in meters to kilometers
   * @param {number} value - Distance in meters
   * @returns {string} Formatted distance string or 'N/A'
   */
  const formatDistance = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';
    const km = value / 1000;
    return `${km.toFixed(2)} km`;
  };

  /**
   * Format distance in meters to nautical miles
   * @param {number} value - Distance in meters
   * @returns {string} Formatted distance string or 'N/A'
   */
  const formatDistanceNM = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';
    const nm = value / 1852;
    return `${nm.toFixed(2)} NM`;
  };

  /**
   * Format duration in seconds to hours and minutes
   * @param {number} seconds - Duration in seconds
   * @returns {string} Formatted duration string (Xh Ym) or 'N/A'
   */
  const formatRunningSeconds = (seconds) => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  /**
   * Format duration in seconds to HH:MM:SS format
   * @param {number|string} seconds - Duration in seconds
   * @returns {string} Formatted duration string (HH:MM:SS) or 'N/A'
   */
  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';

    // Parse to number and floor to remove decimals
    const totalSeconds = Math.floor(parseFloat(seconds));

    if (Number.isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  /**
   * Format voyage duration from start and end times
   * @param {string} startTime - Start time string
   * @param {string} endTime - End time string
   * @returns {string} Formatted duration string (HH:MM:SS) or 'N/A'
   */
  const formatVoyageDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    const start = moment(startTime);
    const end = moment(endTime);
    const durationMs = end.diff(start);
    const totalHours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    return `${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  /**
   * Get CSS class for sea state based on value
   * @param {string} seaState - Sea state value
   * @returns {string} CSS class name
   */
  const getSeaStateClass = (seaState) => {
    if (!seaState) return 'sea-state-default';
    const state = seaState.toUpperCase();
    if (state === 'CALM') return 'sea-state-calm';
    if (state === 'SLIGHT') return 'sea-state-slight';
    if (state === 'MODERATE') return 'sea-state-moderate';
    if (state === 'ROUGH') return 'sea-state-rough';
    if (state === 'VERY ROUGH') return 'sea-state-very-rough';
    if (state === 'HIGH') return 'sea-state-high';
    if (state === 'VERY HIGH') return 'sea-state-very-high';
    return 'sea-state-default';
  };

  /**
   * Get CSS class for sea state based on value (text color only)
   * @param {string} seaState - Sea state value
   * @returns {string} CSS class name for text color
   */
  const getSeaStateTextClass = (seaState) => {
    if (!seaState) return 'text-secondary';
    const state = seaState.toUpperCase();
    if (state === 'CALM') return 'text-success';
    if (state === 'SLIGHT') return 'text-success';
    if (state === 'MODERATE') return 'text-warning';
    if (state === 'ROUGH') return 'text-warning';
    if (state === 'VERY ROUGH') return 'text-danger';
    if (state === 'HIGH') return 'text-danger';
    if (state === 'VERY HIGH') return 'text-danger';
    return 'text-secondary';
  };

  return {
    formatIndonesianDate,
    formatNumber,
    formatDistance,
    formatDistanceNM,
    formatRunningSeconds,
    formatDuration,
    formatVoyageDuration,
    getSeaStateClass,
    getSeaStateTextClass,
  };
}

export default useVoyageFormatters;
